// VajraShop.jsx — Phase 59C
// 如意宝法宝流通 — 流通 ≠ 商店: 事件驱动，人工对接，无购物车/支付

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../constants/config';

const GD  = '#8B6914';
const GDH = '#D4AF37';
const BG  = '#FDFBF7';

const CATEGORY_LABELS = {
  vase:       '宝瓶',
  instrument: '法器',
  text:       '法本',
  thangka:    '唐卡',
  mala:       '念珠',
  other:      '其他',
};

export default function VajraShop({ lang }) {
  const L = (zh, en) => lang === "en" ? en : zh;
  const [products, setProducts]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeCategory, setCategory] = useState('all');
  const navigate = useNavigate();

  const token   = localStorage.getItem('vajra_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/vajra/shop/products`, { headers });
        if (res.ok) {
          const d = await res.json();
          setProducts(d.products || []);
        }
      } catch (e) {
        console.error('VajraShop load error:', e);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = ['all', ...Array.from(new Set(products.map(p => p.category))).filter(Boolean)];
  const showCategoryNav = products.length > 8;
  const visible = activeCategory === 'all'
    ? products
    : products.filter(p => p.category === activeCategory);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#aaa' }}>{L("加载中...","Loading…")}</div>;
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px 80px', fontFamily: 'system-ui, sans-serif', background: BG, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 36 }}>
        <h1 style={{ fontSize: 26, color: GD, margin: '0 0 6px', fontWeight: 600 }}>{L("如意宝法宝流通","Dharma Shop")}</h1>
        <p style={{ fontSize: 13, color: 'rgba(139,105,20,0.45)', margin: 0, letterSpacing: 1 }}>
          Vajra Treasure Circulation
        </p>
      </div>

      {/* 分类导航 — 仅 > 8 件时显示 */}
      {showCategoryNav && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              style={{
                padding: '5px 16px', borderRadius: 9999, fontSize: 12, cursor: 'pointer',
                border: `1px solid ${activeCategory === cat ? GDH : 'rgba(212,175,55,0.30)'}`,
                background: activeCategory === cat ? GDH : 'transparent',
                color: activeCategory === cat ? '#fff' : 'rgba(139,105,20,0.7)',
              }}>
              {cat === 'all' ? L('全部','All') : (CATEGORY_LABELS[cat] || cat)}
            </button>
          ))}
        </div>
      )}

      {/* 当前流通 — 空状态 */}
      {visible.length === 0 && (
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <p style={{ fontSize: 15, color: 'rgba(139,105,20,0.3)', margin: '0 0 8px' }}>
            {L("当前无法宝在流通","No items available at this time")}
          </p>
          <p style={{ fontSize: 12, color: 'rgba(139,105,20,0.2)', margin: '0 0 20px' }}>
            {L("法宝将在法会·共修后出现","Items will appear after dharma events and practice sessions")}
          </p>
          <a href="/vajra/calendar" style={{ fontSize: 12, color: GD, textDecoration: 'none' }}>
            {L("浏览往期流通记录 →","Browse calendar →")}
          </a>
        </div>
      )}

      {/* 法宝卡片列表 */}
      {visible.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
          {visible.map(p => (
            <ProductCard key={p.id} product={p} navigate={navigate} lang={lang} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProductCard({ product: p, navigate, lang }) {
  const [hovered, setHovered] = useState(false);

  const statusLabel = { available: '可请', reserved: '待确认', sold: '已请' }[p.status] || p.status;
  const statusStyle = p.status === 'available'
    ? { background: 'rgba(139,105,20,0.12)', color: GD }
    : { background: 'rgba(0,0,0,0.04)', color: '#999' };

  return (
    <div
      onClick={() => navigate(`/vajra/shop/${p.id}`)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff',
        border: '1px solid rgba(212,175,55,0.15)',
        borderRadius: 12,
        padding: 16,
        cursor: 'pointer',
        position: 'relative',
        transition: 'box-shadow 0.2s',
        boxShadow: hovered ? '0 4px 16px rgba(0,0,0,0.10)' : '0 2px 8px rgba(0,0,0,0.06)',
      }}>
      {/* 结缘标记 — 低调右上角小字，无高亮/边框/emoji */}
      {p.is_recommended && (
        <div style={{
          position: 'absolute', top: 10, right: 12,
          fontSize: 11, color: 'rgba(139,105,20,0.3)',
        }}>
          与您结缘
        </div>
      )}

      {/* 图片 */}
      {p.image_url && (
        <img src={p.image_url} alt={lang === 'en' && p.name_en ? p.name_en : p.name_zh}
          style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 8, marginBottom: 12 }} />
      )}
      {!p.image_url && (
        <div style={{ width: '100%', height: 100, background: 'rgba(139,105,20,0.04)', borderRadius: 8, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 28, opacity: 0.3 }}>🏺</span>
        </div>
      )}

      {/* 分类 */}
      <div style={{ fontSize: 10, color: 'rgba(139,105,20,0.45)', letterSpacing: 1, marginBottom: 4, textTransform: 'uppercase' }}>
        {CATEGORY_LABELS[p.category] || p.category}
      </div>

      {/* 名称 */}
      <div style={{ fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 6 }}>{lang === 'en' && p.name_en ? p.name_en : p.name_zh}</div>

      {/* 描述摘要 */}
      {(lang === 'en' && p.description_en ? p.description_en : p.description_zh) && (
        <p style={{ fontSize: 12, color: '#888', margin: '0 0 10px', lineHeight: 1.6,
          overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {lang === 'en' && p.description_en ? p.description_en : p.description_zh}
        </p>
      )}

      {/* 底部: 金额 + 状态 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
        {p.suggested_amount_cad ? (
          <span style={{ fontSize: 13, color: GD }}>建议供养 CAD {p.suggested_amount_cad}</span>
        ) : (
          <span style={{ fontSize: 12, color: 'rgba(139,105,20,0.3)' }}>随喜供养</span>
        )}
        <span style={{ fontSize: 11, padding: '2px 9px', borderRadius: 9999, ...statusStyle }}>
          {statusLabel}
        </span>
      </div>
    </div>
  );
}
